/**
 * Spedizione di un ordine arrivato da un negozio (Shopify / WooCommerce del motore): corriere e numero di
 * tracciamento vanno al negozio, che segna l'ordine come evaso (core api/shipmentRoutes.ts). Per questi
 * ordini il tracciamento non si scrive a mano sull'ordine: passa da qui, cosi' il negozio lo sa.
 * Si mostra al posto di OrderTrackingSection solo per gli ordini con commerce_store_id.
 */
import { useState } from 'react';
import { NotionButton } from '../../ui/notion';
import { SHIPMENT_STATUS_TEXT, stockApi } from '../../../lib/commerce/stockApi';
import { useCommerceAction } from '../../../lib/commerce/useCommerceAction';
import { useCommerceQuery } from '../../../lib/commerce/useCommerceQuery';
import { formatQuantity } from '../../../lib/utils/quantityFormat';

export function StoreShipmentSection({ orderId, onShipped }: { orderId: string; onShipped?: () => void }) {
  const q = useCommerceQuery(() => stockApi.getShipments(orderId), `shipments-${orderId}`);
  const { busy, run } = useCommerceAction();
  const [open, setOpen] = useState(false);
  const [carrier, setCarrier] = useState('');
  const [number, setNumber] = useState('');
  const [url, setUrl] = useState('');
  const [noTracking, setNoTracking] = useState(false);
  const [notify, setNotify] = useState(true);

  const d = q.data;
  if (!d) return null;
  const openLines = d.lines.filter((l) => l.open > 0);
  const canShip = d.available && openLines.length > 0 && d.order.status !== 'cancelled';

  const ship = async () => {
    const r = await run('ship', () => stockApi.ship(orderId, {
      carrier: carrier.trim() || null, tracking_number: noTracking ? null : number.trim() || null,
      tracking_url: noTracking ? null : url.trim() || null, without_tracking: noTracking, notify_customer: notify,
    }), { success: 'Spedizione registrata: la comunichiamo al negozio.' });
    if (r.ok) {
      setOpen(false); setNumber(''); setUrl('');
      await q.reload();
      onShipped?.();
    }
  };

  return (
    <div className="py-2 space-y-2">
      {d.shipments.map((s) => (
        <div key={s.id} className="text-sm">
          <span className="font-medium text-[#37352F] dark:text-white">{s.carrier ?? 'Senza corriere'}</span>
          {s.tracking_numbers.length > 0 && <span className="text-[#787774]"> · {s.tracking_numbers.join(', ')}</span>}
          <span className="ml-2 text-xs text-[#9B9A97]">{SHIPMENT_STATUS_TEXT[s.status] ?? s.status}</span>
          {s.last_error && s.status === 'error' && <p className="text-xs text-red-600">{s.last_error}</p>}
        </div>
      ))}
      {!d.available && d.shipments.length === 0 && (
        <p className="text-xs text-[#9B9A97]">La spedizione verso il negozio arriva con un prossimo aggiornamento di Flusia.</p>
      )}
      {canShip && !open && (
        <NotionButton size="sm" variant="secondary" onClick={() => setOpen(true)}>
          Segna come spedito ({formatQuantity(openLines.reduce((n, l) => n + l.open, 0), { unit: 'pz', unitDisplay: 'none' })} {openLines.reduce((n, l) => n + l.open, 0) === 1 ? 'pezzo' : 'pezzi'})
        </NotionButton>
      )}
      {canShip && open && (
        <div className="rounded-md border border-[rgba(55,53,47,0.09)] dark:border-gray-700 p-3 space-y-2">
          <label className="block text-sm">
            <span className="text-[#37352F] dark:text-gray-200">Corriere</span>
            <input list={`carriers-${orderId}`} value={carrier} onChange={(e) => setCarrier(e.target.value)} placeholder="BRT, GLS Italy, SDA..."
              className="mt-1 w-full rounded-md border border-[rgba(55,53,47,0.16)] bg-white dark:bg-gray-800 px-2 py-1.5 text-sm" />
            <datalist id={`carriers-${orderId}`}>{d.carriers.map((c) => <option key={c} value={c} />)}</datalist>
          </label>
          {!noTracking && (
            <>
              <label className="block text-sm">
                <span className="text-[#37352F] dark:text-gray-200">Numero di tracciamento</span>
                <input value={number} onChange={(e) => setNumber(e.target.value)}
                  className="mt-1 w-full rounded-md border border-[rgba(55,53,47,0.16)] bg-white dark:bg-gray-800 px-2 py-1.5 text-sm" />
              </label>
              <label className="block text-sm">
                <span className="text-[#37352F] dark:text-gray-200">Link di tracciamento (serve per SDA e corrieri locali)</span>
                <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://..."
                  className="mt-1 w-full rounded-md border border-[rgba(55,53,47,0.16)] bg-white dark:bg-gray-800 px-2 py-1.5 text-sm" />
              </label>
            </>
          )}
          <label className="flex items-center gap-2 text-sm text-[#37352F] dark:text-gray-200">
            <input type="checkbox" checked={noTracking} onChange={(e) => setNoTracking(e.target.checked)} />
            Spedisci senza tracciamento
          </label>
          <label className="flex items-center gap-2 text-sm text-[#37352F] dark:text-gray-200">
            <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} />
            Il negozio avvisa il cliente della spedizione
          </label>
          <div className="flex justify-end gap-2">
            <NotionButton size="sm" variant="ghost" onClick={() => setOpen(false)}>Annulla</NotionButton>
            <NotionButton size="sm" variant="primary" loading={busy === 'ship'} disabled={!noTracking && !number.trim()} onClick={() => void ship()}>
              Registra la spedizione
            </NotionButton>
          </div>
        </div>
      )}
    </div>
  );
}

export default StoreShipmentSection;
